/*
Maximum number of pairs of parentheses we're allowing a url-pattern to have. We want this number to not be
 too high to keep the performance of `isPrecedenceClashingPair` reasonable.
 */
const MAX_PAREN_PAIRS = 5;

const pathPartPattern = /^:?[0-9a-z_-]+$/i;

const terminalPathPartPattern = /^((:|\*)?[0-9a-z_-]+)?$/i;

/**
 * @param urlPattern a string in Backbone url-pattern-matching language.
 * (See here: https://backbonejs.org/#Routing)
 *
 * @returns a boolean, indicating if that url-pattern is well-formed.
 *
 * Note that the definition of 'well-formed' encoded by this function may be more restrictive than what makes
 * sense in Backbone pattern-matching language in full generality -- the language is not specified
 * rigorously enough to know.
 *
 * See tests in ./__tests__/isWellFormedUrlPattern-test.ts for some more indications of what patterns are
 * well-formed.
 */
export default function isWellFormedUrlPattern(urlPattern: string): boolean {
  if (!areParenthesesWellFormed(urlPattern)) {
    return false;
  }
  const pathParts = urlPattern.replace(/[()]/g, '').split('/');
  return pathParts.reduce((accumBool, currentPattern, index) => {
    if (index === pathParts.length - 1) {
      return accumBool && terminalPathPartPattern.test(currentPattern);
    } else {
      return accumBool && pathPartPattern.test(currentPattern);
    }
  }, true);
}

/**
 * Tests that parentheses appear in syntactically correct positions in the input `urlPattern`.
 *
 * What is allowed: non nested, balanced parentheses, that do not contain parts of more than one path-part,
 * that meet one of the following conditions:
 * Let x's left-non-paren-neighbour be the closest neighbour of char x to the left that is not a parenthesis.
 * Same for x's right-non-paren-neighbour, mutatis mutandis.
 * 1. left-paren's left-non-paren-neighbour is a slash, right-paren is immeidately preceded by a slash, and
 * neither paren's right-non-paren-neighbour is a slash.
 * 2. neither left nor right paren's left-non-paren-neighbour is a slash, left-paren is immediately followed
 * by a slash.
 * 3. left parenthesis at the beginning, right parenthesis immediately preceded by slash.
 * 4. right paren at the end, left paren either immediately preceded by or immediately followed by slash.
 * 5. left parenthesis at the beginning, right parenthesis at the end.
 */
function areParenthesesWellFormed(urlPattern: string): boolean {
  /*
  To keep track of total number of paren pairs. We don't want them to exceed MAX_PAREN_PAIRS, to keep the
  performance of `isPrecedenceClashingPair` reasonable.
  */
  let leftParenCount = 0;
  // To keep track of nesting and balance of parentheses.
  let parenBalanceCount = 0;
  /*
  slashBefore === true` when we should expect a slash to come before the next right parenthesis, given
  it's not at the end of the pattern, and given what we've seen at previous parentheses.
  `slashBefore === false` when we should expect a slash to come after the right parenthesis, given it's not
  at the end of the pattern, and given what we've seen at previous parentheses.
  */
  let slashBefore = null;
  for (let i = 0; i < urlPattern.length; i++) {
    const current = urlPattern[i];
    const before = urlPattern[i - 1];
    const after = urlPattern[i + 1];
    switch (current) {
      case '(':
        leftParenCount += 1;
        if (leftParenCount > MAX_PAREN_PAIRS) {
          return false;
        }
        parenBalanceCount += 1;
        // If before === ')', inherit `slashBefore` property from previous, adjacent parentheses.
        if (before !== ')') {
          if (i === 0 || before === '/') {
            slashBefore = true;
          } else if (after === '/') {
            slashBefore = false;
          } else {
            // Left bracket is somewhere it shouldn't be.
            return false;
          }
        } else if (slashBefore === false) {
          if (after !== '/') {
            return false;
          }
        }
        break;
      case ')':
        parenBalanceCount -= 1;
        if (i !== urlPattern.length - 1) {
          switch (slashBefore) {
            case true:
              if (before !== '/') {
                return false;
              }
              break;
            case false:
              /* If `after === '('`, appropriate placement of next slash will be checked in the next set of
              parentheses */
              if (before === '/' || (after !== '/' && after !== '(')) {
                return false;
              }
              break;
            default:
              /*
              `slashBefore` wasn't set correctly, indicating a right bracket without a matching left.
              Redundant given other code, but we know we need to return `false` if we're in this default
              block.
              */
              return false;
          }
        }
        break;
      case '/':
        // Making sure that the parens don't contain parts of more than one path-part.
        if (parenBalanceCount > 0) {
          if (before !== '(' && after !== ')') {
            return false;
          }
        } else {
          slashBefore = null;
        }
        break;
      default:
        if (parenBalanceCount <= 0) {
          slashBefore = null;
        }
    }
    // `parenCount` outside 0-1 range indicates nested or imbalanced brackets.
    if (parenBalanceCount < 0 || parenBalanceCount > 1) {
      return false;
    }
  }
  // If `bracketCount` does not equal 0 at the end, brackets are imbalanced.
  return parenBalanceCount === 0;
}
